import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal, ModalBody, Button, Input, Spinner, MultiSelect } from '@components/ui';
import { useCreateUser, useRoles, useBranchOffices } from '../hooks/useUsers';
import { useCompanies } from '@features/companies/hooks/useCompanies';
import { useConsumption } from '@features/subscriptions/hooks';
import Swal from 'sweetalert2';

const createUserSchema = z.object({
  email: z.string().email('Email inválido').max(100, 'Máximo 100 caracteres'),
  idNumber: z.string().min(1, 'La cédula es requerida').max(20, 'Máximo 20 caracteres'),
  firstName: z.string().min(1, 'El nombre es requerido').max(100, 'Máximo 100 caracteres'),
  lastName: z.string().min(1, 'El apellido es requerido').max(100, 'Máximo 100 caracteres'),
  roles: z.array(z.string()).min(1, 'Debe seleccionar al menos un rol'),
  branchOffices: z.array(z.string()).optional(),
});

type CreateUserFormData = z.infer<typeof createUserSchema>;

interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CreateUserModal({ isOpen, onClose }: CreateUserModalProps) {
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedBranchOffices, setSelectedBranchOffices] = useState<string[]>([]);

  // Roles permitidos para crear usuarios
  const ALLOWED_ROLES = ['VENDEDOR', 'CUSTOMER B2B', 'COMPANY ADMIN', 'ADMINISTRATOR'];

  const { data: rolesData, isLoading: loadingRoles } = useRoles();
  const { data: branchOfficesData, isLoading: loadingBranchOffices } = useBranchOffices();
  const { selectedCompany } = useCompanies({ navigateOnSelect: false });
  const createMutation = useCreateUser();

  // Subscription and consumption validation
  const {
    validateCanAddUser,
    consumption,
    currentPlan,
    getRemainingUsers,
    isLoading: isLoadingConsumption,
  } = useConsumption();

  // Filtrar solo los roles permitidos (case-insensitive) y eliminar duplicados por nombre
  const filteredRoles = rolesData?.filter(role =>
    ALLOWED_ROLES.some(allowedRole => allowedRole.toUpperCase() === role.name.toUpperCase())
  ).reduce((unique, role) => {
    // Only add if we haven't seen this role name before (case-insensitive)
    if (!unique.some(r => r.name.toUpperCase() === role.name.toUpperCase())) {
      unique.push(role);
    }
    return unique;
  }, [] as typeof rolesData) || [];

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<CreateUserFormData>({
    resolver: zodResolver(createUserSchema),
  });


  const onSubmit = async (data: CreateUserFormData) => {
    try {
      // Validate plan limits before creating user
      const validation = validateCanAddUser();

      if (!validation.allowed) {
        const result = await Swal.fire({
          title: 'Límite de usuarios alcanzado',
          text: validation.reason,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Ver planes',
          cancelButtonText: 'Cancelar',
          confirmButtonColor: '#3085d6',
        });

        if (result.isConfirmed) {
          // Navigate to plans page
          window.location.href = '/subscriptions/plans';
        }
        return;
      }

      await createMutation.mutateAsync({
        ...data,
        roles: selectedRoles,
        branchOffices: selectedBranchOffices,
      });

      await Swal.fire({
        title: 'Usuario creado',
        text: 'El usuario ha sido creado exitosamente. Se ha enviado un correo con las instrucciones para establecer la contraseña.',
        icon: 'success',
        confirmButtonText: 'Aceptar',
      });

      reset();
      setSelectedRoles([]);
      setSelectedBranchOffices([]);
      onClose();
    } catch (error: any) {
      Swal.fire({
        title: 'Error',
        text: error.message || 'No se pudo crear el usuario',
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
    }
  };

  const handleClose = () => {
    reset();
    setSelectedRoles([]);
    setSelectedBranchOffices([]);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalBody>
        <div className="px-6 py-4 max-h-[80vh] overflow-y-auto">
          <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-6">
            Crear Nuevo Usuario
          </h2>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Información de la Empresa */}
            <div className="bg-secondary-50 dark:bg-secondary-800 p-4 rounded-lg">
              <h3 className="text-sm font-medium text-secondary-900 dark:text-white mb-2">
                Empresa
              </h3>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">
                {selectedCompany?.name || 'No hay empresa seleccionada'}
              </p>
              <p className="text-xs text-secondary-500 dark:text-secondary-500 mt-1">
                El usuario será creado para esta empresa
              </p>
            </div>

            {/* Plan Information */}
            {!isLoadingConsumption && consumption && currentPlan && (
              <div className="bg-primary-50 dark:bg-primary-900/20 p-4 rounded-lg border border-primary-200 dark:border-primary-800">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-primary-900 dark:text-primary-100 mb-1">
                      Plan Actual: {currentPlan.name}
                    </h3>
                    <p className="text-xs text-primary-700 dark:text-primary-300">
                      {consumption.UserQty} de {currentPlan.limits.usuarios === -1 ? 'ilimitados' : currentPlan.limits.usuarios} usuarios utilizados
                    </p>
                  </div>
                  {(() => {
                    const remaining = getRemainingUsers();
                    if (remaining === -1) {
                      return (
                        <div className="text-right">
                          <p className="text-lg font-semibold text-primary-900 dark:text-primary-100">
                            ∞
                          </p>
                          <p className="text-xs text-primary-700 dark:text-primary-300">
                            ilimitado
                          </p>
                        </div>
                      );
                    }
                    if (remaining !== null && !isNaN(remaining)) {
                      return (
                        <div className="text-right">
                          <p className="text-lg font-semibold text-primary-900 dark:text-primary-100">
                            {remaining}
                          </p>
                          <p className="text-xs text-primary-700 dark:text-primary-300">
                            restantes
                          </p>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>
            )}

            {/* Información Personal */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-secondary-900 dark:text-white">
                Información Personal
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Nombre"
                  placeholder="Juan"
                  error={errors.firstName?.message}
                  maxLength={100}
                  {...register('firstName')}
                />
                <Input
                  label="Apellido"
                  placeholder="Pérez"
                  error={errors.lastName?.message}
                  maxLength={100}
                  {...register('lastName')}
                />
                <Input
                  label="IDNumber"
                  placeholder="8-888-8888"
                  error={errors.idNumber?.message}
                  maxLength={20}
                  {...register('idNumber')}
                />
                <Input
                  label="Email"
                  type="email"
                  placeholder="usuario@ejemplo.com"
                  error={errors.email?.message}
                  maxLength={100}
                  {...register('email')}
                />
              </div>
            </div>

            {/* Roles */}
            <MultiSelect
              label="Roles *"
              options={filteredRoles.map((role, index) => ({
                id: role.id || `role-${role.name}-${index}`,
                label: role.name,
                description: role.description,
              }))}
              selected={selectedRoles}
              onChange={(values) => {
                setSelectedRoles(values);
                setValue('roles', values);
              }}
              placeholder="Selecciona los roles"
              error={errors.roles?.message}
              loading={loadingRoles}
            />

            {/* Sucursales */}
            {branchOfficesData && branchOfficesData.length > 0 ? (
              <MultiSelect
                label="Sucursales (opcional)"
                options={branchOfficesData.map((branchOffice) => ({
                  id: branchOffice.Id,
                  label: branchOffice.Name,
                  description: branchOffice.Code ? `Código: ${branchOffice.Code}` : undefined,
                }))}
                selected={selectedBranchOffices}
                onChange={(values) => {
                  setSelectedBranchOffices(values);
                  setValue('branchOffices', values);
                }}
                placeholder="Selecciona las sucursales"
                error={errors.branchOffices?.message}
                loading={loadingBranchOffices}
              />
            ) : (
              <div className="space-y-2">
                <label className="text-sm font-medium text-secondary-900 dark:text-white">
                  Sucursales (opcional)
                </label>
                {loadingBranchOffices ? (
                  <div className="flex items-center justify-center py-8 border border-secondary-300 dark:border-secondary-600 rounded-lg">
                    <Spinner size="sm" />
                  </div>
                ) : (
                  <div className="text-sm text-secondary-500 dark:text-secondary-400 p-4 border border-secondary-300 dark:border-secondary-600 rounded-lg text-center">
                    No hay sucursales disponibles para esta empresa
                  </div>
                )}
              </div>
            )}

            {/* Botones */}
            <div className="flex justify-end gap-3 pt-4 border-t border-secondary-200 dark:border-secondary-700">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={createMutation.isPending}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={createMutation.isPending || loadingRoles || loadingBranchOffices}
              >
                {createMutation.isPending ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    Creando...
                  </>
                ) : (
                  'Crear Usuario'
                )}
              </Button>
            </div>
          </form>
        </div>
      </ModalBody>
    </Modal>
  );
}
